/**
 * 보드 카드 하나.
 *
 * **줄이 둘이고 역할이 갈린다**(2026-08-26 지시).
 *
 * - **제목 줄** — 번호와 제목**만**. 카드를 고를 때 읽는 것이 그 둘이고,
 *   여기에 아이콘·아바타가 섞이면 제목이 시작하는 자리가 카드마다 밀린다.
 * - **속성 줄** — 나머지 전부(타입·우선순위·마감·라벨·담당자).
 *
 * **본문은 카드에 없다.** 상세에만 있다 — 카드에 본문을 몇 줄 흘리면
 * 카드 높이가 제각각이 되고, 한 화면에 들어오는 장수가 절반이 된다.
 *
 * 속성이 하나도 없으면 **속성 줄이 통째로 사라진다.** 방금 낸 이슈가
 * 그런 카드이고, 이 도구에서 가장 흔하다.
 */

import type { CSSProperties } from 'react';

import type { TicketCard } from '../api';
import { deviceLabel, environmentLabel, priorityOf, workSpan } from '../columns';
import {
  AssigneeMark,
  BADGE,
  DueMark,
  LabelChip,
  PriorityMark,
  toneVars,
  TypeMark,
} from './Chips';

export function TicketCardView({
  ticket,
  draggable,
  onOpen,
  onDragStart,
  onDrop,
  lead,
}: {
  ticket: TicketCard;
  draggable: boolean;
  onOpen: () => void;
  onDragStart?: (event: React.DragEvent<HTMLElement>) => void;
  onDrop?: (event: React.DragEvent<HTMLElement>) => void;
  /**
   * 1열 둘째 자리. **안 주면 우선순위**가 선다(업무 보드).
   *
   * 배포 보드가 여기에 배포 상태를 넣는다 — 그 보드에서 묻는 것은
   * 「이게 어디까지 나갔나」이지 「얼마나 급한가」가 아니다.
   */
  lead?: React.ReactNode;
}) {
  const hasMeta =
    (ticket.col === 'in_progress' && ticket.started_at !== null) ||
    ticket.device !== null ||
    ticket.environment !== null ||
    ticket.type !== null ||
    ticket.assignee !== null ||
    ticket.due_date !== null ||
    ticket.labels.length > 0;

  /*
    ⚠ **보류를 카드 이름에도 적는다.** `aria-label` 은 안쪽 글자를 **덮는다** —
    아래 배지를 그려 놓아도 낭독기에는 「PLUS-118 제목」만 들린다.
  */
  const cardLabel = ticket.held_at
    ? `보류 ${ticket.key} ${ticket.title}`
    : `${ticket.key} ${ticket.title}`;

  return (
    <article
      className="card"
      /* 왼쪽 줄무늬가 우선순위다(2026-10-18 C 현황판형). 안 정했으면 회색. */
      style={{ '--p': priorityOf(ticket.priority) ? `var(--st-${priorityOf(ticket.priority)!.tone}-f)` : 'var(--line-2)' } as CSSProperties}
      data-ticket-id={ticket.id}
      draggable={draggable}
      tabIndex={0}
      role="button"
      aria-label={cardLabel}
      onDragStart={onDragStart}
      onDragOver={onDrop ? (event) => event.preventDefault() : undefined}
      onDrop={onDrop}
      onClick={onOpen}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onOpen();
        }
      }}
    >
      {/*
        **행이 둘이다**(2026-08-29 지시). 1행은 보드마다 다르고 2행은 늘
        제목이다 — 두 보드의 카드가 같은 뼈대를 갖되 윗줄만 갈린다.

        | | 업무 칸반 | 배포 보드 |
        |:--|:--|:--|
        | **1행** | 번호 · 우선순위 | 번호 · 배포 상태 |
        | **2행** | 제목 | 제목 |

        **제목이 카드 전체 폭을 쓴다.** 번호와 배지를 제목과 한 줄에 늘어
        놓던 옛 판은 좁은 칸에서 제목에 남는 폭이 없어 **한 글자씩 세로로
        쪼개졌다**(실측). 왼쪽 열로 모아 봐도 제목 폭이 좁기는 마찬가지였다 —
        위아래로 가르는 것이 답이다.
      */}
      <div className="flex flex-col gap-s1">
        {/* **번호는 왼쪽, 우선순위·배포 상태는 오른쪽 끝**(2026-08-29
            지시). 왼쪽부터 줄줄이 붙여 놓으면 카드마다 오른쪽 것의
            시작점이 번호 길이에 따라 밀려 세로로 안 맞는다. */}
        <div className="flex items-center gap-s2">
          {/*
            **번호 배지가 유형(이슈/할 일)도 함께 말한다.** 「이슈」라는
            딱지를 따로 붙이면 줄이 하나 늘거나 제목과 자리를 다투는데,
            그 축은 값이 둘뿐이라 그만한 값을 안 한다.

            할 일은 **아무 색도 안 준다** — 기본값이라서다. 예외인 이슈만
            붉어진다. 색만으로 가르지 않도록 `title` 에 이름을 적는다.
          */}
          <span
            className={[
              'card-key tnum',
              ticket.kind === 'issue' ? 'text-st-red-f' : 'text-ink',
            ].join(' ')}
            title={ticket.kind === 'issue' ? '이슈' : '할 일'}
          >
            {ticket.key}
          </span>

          {/*
            **보류는 번호 바로 옆이다**(2026-09-02 사용자 확정). 미룬 카드가
            한눈에 갈려야 하는데, 속성 줄(2행)은 속성이 하나도 없으면 통째로
            사라져서 **방금 낸 이슈를 미루면 표시가 안 나온다** — 그 카드가
            이 도구에서 가장 흔하다.

            ⚠ **배지 하나로 끝낸다.** 카드를 흐리게 하거나 테두리를 바꾸는
            길도 있지만, 흐림은 「불러오는 중」으로 읽히고 이 저장소는 이미
            그 방식이 켜짐/꺼짐을 못 가른다는 것을 실측했다(`LabelChip`).

            사유는 `title` 에만 둔다 — 카드에 글로 풀면 줄이 하나 늘고,
            그 줄은 미룬 카드에만 있어 카드 높이가 제각각이 된다.
            **낭독기는 위 `cardLabel` 이 말한다** — 이 배지 글자는 안 들린다.
          */}
          {ticket.held_at && (
            <span
              className={BADGE}
              style={toneVars('amber')}
              title={ticket.hold_reason ? `보류 — ${ticket.hold_reason}` : '보류'}
            >
              보류
            </span>
          )}


          {/*
            1행 오른쪽. **부르는 쪽이 정한다** — 배포 보드는 배포 상태를
            넣고, 안 주면 우선순위가 선다(업무 보드).

            `ml-auto` 가 남는 폭을 다 먹어 오른쪽 끝으로 민다. 값이 없으면
            (우선순위를 안 정했다) 아무것도 안 그린다 — 빈 자리를 지키는
            것보다 없는 편이 조용하다.
          */}
          <span className="ml-auto shrink-0">
            {lead ?? <PriorityMark priority={ticket.priority} />}
          </span>
        </div>

        <span className="text-body leading-tight break-keep [overflow-wrap:anywhere]">
          {ticket.title}
        </span>
      </div>

      {hasMeta && (
        <div className="mt-s2 flex flex-wrap items-center gap-s2">
          <TypeMark type={ticket.type} />
          <DueMark due={ticket.due_date} />
          {/*
            작업 기간. **「작업 중」 칸에서만 보인다** — 다른 칸에서는 그
            날짜가 아직 뜻이 없거나(대기) 이미 지난 이야기다(완료).

            예정 일수가 없으면 시작일만 나온다. 그것이 「아직 안 적었다」를
            말하는 자리이기도 하다 — 빈칸이면 적을 것이 있는지도 모른다.
          */}
          {ticket.col === 'in_progress' && ticket.started_at && (
            <span className={`${BADGE} tnum`} style={toneVars('gray')}>
              {workSpan(ticket.started_at, ticket.estimate_days)}
            </span>
          )}
          {/* 이슈에만 있다. 「어디서 났나」는 고치는 사람이 가장 먼저 묻는 것이다. */}
          {(ticket.device || ticket.environment) && (
            <span className={BADGE} style={toneVars('gray')}>
              {[deviceLabel(ticket.device), environmentLabel(ticket.environment)]
                .filter(Boolean)
                .join(' · ')}
            </span>
          )}
          {ticket.labels.map((label) => (
            <LabelChip key={label.id} label={label} />
          ))}
          {/* 담당자는 **오른쪽 끝**이다. 자리가 고정돼야 카드를 훑을 때
              눈이 세로로 곧게 내려간다. */}
          <span className="ml-auto flex items-center">
            <AssigneeMark user={ticket.assignee} />
          </span>
        </div>
      )}
    </article>
  );
}
